import { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useOutletContext } from "react-router-dom";
import CscSubLogo from "Assets/Img/CscSubLogo";
import BellIcons from "Assets/Img/BellIcon";
import useLogout from "hooks/useLogout";
import useSwitchBackToAdmin from "hooks/useSwitchBackToAdmin";
import { isImpersonating } from "utils/Common/impersonation";
import { getUnreadCount } from "services/notificationService";
import useRefetchOnFocus from "hooks/useRefetchOnFocus";
import { useNavigate } from "react-router-dom";
import "./InspectorFi/styles/inspector.scss";
import Button from "Components/Common/Button/Button";

interface Crumb {
  label: string;
  link?: string;
}

export interface InspectorOutletContext {
  setCrumbs: (crumbs: Crumb[]) => void;
}

export function useInspectorLayout() {
  return useOutletContext<InspectorOutletContext>();
}

export default function InspectorLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { handleLogout, loggingOut } = useLogout();
  const { switchBackToAdmin } = useSwitchBackToAdmin();
  const [showBackToAdmin] = useState(isImpersonating());
  const [crumbs, setCrumbs] = useState<Crumb[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [countLoading, setCountLoading] = useState(true);

  const isInspectionPage = location.pathname === "/project-inspection";

  const fetchCount = async () => {
    setCountLoading(true);
    try {
      const res: any = await getUnreadCount();
      setUnreadCount(res?.data?.data?.count ?? 0);
    } catch {} finally {
      setCountLoading(false);
    }
  };

  useRefetchOnFocus(fetchCount);

  useEffect(() => {
    fetchCount();
    const handler = () => {
      fetchCount();
    };
    window.addEventListener("notification-unread-count-changed", handler);
    return () =>
      window.removeEventListener("notification-unread-count-changed", handler);
  }, []);

  return (
    <>
      {!isInspectionPage ? (
        <div
          className="inspectorPage"
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100vh",
            overflow: "hidden",
          }}
        >
          <div className="topBar">
            <div className="tbLogo">
              <CscSubLogo />
            </div>
            <div className="tbInfo">
              {crumbs.length > 0 && (
                <div className="tbCrumbs">
                  {crumbs.map((c, i) => (
                    <span key={i}>
                      {c.link ? (
                        <Link to={c.link} className="tbCrumbLink">
                          {c.label}
                        </Link>
                      ) : (
                        <span className="tbCrumbCurrent">{c.label}</span>
                      )}
                      {i < crumbs.length - 1 && (
                        <span className="tbCrumbSep"> › </span>
                      )}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="tbRight">
              <div
                className="notifySec"
                onClick={() => navigate("/inspector-notifications")}
                style={{ cursor: "pointer" }}
              >
                <BellIcons />
                {countLoading ? (
                  <div className="bellbadgeWrapper">
                    <div className="bellbadge"><span className="btn-spinner" /></div>
                  </div>
                ) : unreadCount > 0 ? (
                  <div className="bellbadgeWrapper">
                    <div className="bellbadge">{unreadCount > 9 ? "9+" : unreadCount}</div>
                  </div>
                ) : null}
              </div>
              {showBackToAdmin && (
                <Button
                  className="backToAdminBtn"
                  onClick={switchBackToAdmin}
                >
                  Back to Admin
                </Button>
              )}
              <Button
                className="signoutBtn"
                onClick={handleLogout}
                disabled={loggingOut}
              >
                {loggingOut && <span className="btn-spinner" />}{" "}
                {loggingOut ? "Signing out" : "Sign Out"}
              </Button>
            </div>
          </div>
          <div style={{ flex: 1, overflowY: "auto" }}>
            <Outlet context={{ setCrumbs } satisfies InspectorOutletContext} />
          </div>
        </div>
      ) : (
        <Outlet context={{ setCrumbs } satisfies InspectorOutletContext} />
      )}
    </>
  );
}
